<template>
  <div class="w-100">
    <div class="w-100 d-flex mb-3 align-items-start filters">
      <FiltersWrap class="flex-fill" />
      <LabelsButton v-if="checkedAssignments.length" class="ms-2" :assignments="checkedAssignments" />
    </div>
    <easy-data-table
      ref="dataTable"
      v-model:server-options="serverOptions"
      v-model:items-selected="checkedAssignments"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :headers="headers"
      :items="items"
      :prevent-context-menu-row="false"
      :fixed-header="false"
      no-hover
      alternating
      hide-footer
      @update:server-options="updateServerOptions"
    >
      <template #loading>
        <b-spinner class="mt-5" variant="success" />
      </template>
      <template #no-data>
        <div style="text-align: center">No data available</div>
      </template>
      <template #item="{ column, item }: { column: string; item: Assignment }">
        <span v-if="column === 'createdAt'">
          <RowDate :date="item.createdAt" />
        </span>
        <span v-if="column === 'reference'">
          <ReferenceCell :assignment="item" />
        </span>
        <span v-if="column === 'customer'">
          <CustomerCell :assignment="item" />
        </span>
        <span v-if="column === 'details'">
          <DetailsCell :assignment="item" />
        </span>
        <span v-if="column === 'features'">
          <FeaturesCell :assignment="item" />
        </span>
        <span v-if="column === 'carrier'">
          <CarriersCell :assignment="item" />
        </span>
        <span v-if="column === 'cost'">
          <CostCell :assignment="item" />
        </span>
        <span v-if="column === 'status'">
          <Status :assignment="item" :short="true" />
          <mdicon
            v-if="hasErrorIcon(item)"
            v-b-tooltip="{ title: `Errors: ${item.totalErrorsCount}`, class: 'error-tooltip' }"
            name="AlertOutline"
          />
        </span>
        <span v-if="column === 'actions'">
          <ActionsCell :assignment="item" />
        </span>
      </template>
    </easy-data-table>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />
  </div>
</template>

<script lang="ts">
import type { Assignment } from '@/types/Models/Assignment'
import { BSpinner } from 'bootstrap-vue-next'
import { defineComponent, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import { computed, type ComputedRef, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAssignmentStore } from '@/store/assignment'
import EasyDataTable, { type Header, type ServerOptions } from 'vue3-easy-data-table'
import Status from '@/views/Components/Assignment/Cells/Status.vue'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import CostCell from '@/views/Components/Assignment/Cells/Cost.vue'
import ActionsCell from '@/views/Components/Assignment/Cells/Actions.vue'
import DetailsCell from '@/views/Components/Assignment/Cells/DetailsCell.vue'
import FiltersWrap from '@/views/Components/Assignment/Filters/FiltersWrap.vue'
import CarriersCell from '@/views/Components/Assignment/Cells/Carriers.vue'
import CustomerCell from '@/views/Components/Assignment/Cells/Customer.vue'
import FeaturesCell from '@/views/Components/Assignment/Cells/Features.vue'
import LabelsButton from '@/views/Components/Assignment/LabelsButton.vue'
import ReferenceCell from '@/views/Components/Assignment/Cells/Reference.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'AssignmentsList',
  components: {
    BSpinner,
    Status,
    TablePagination,
    RowDate,
    CustomerCell,
    LabelsButton,
    CarriersCell,
    ReferenceCell,
    ActionsCell,
    DetailsCell,
    FeaturesCell,
    CostCell,
    FiltersWrap,
    EasyDataTable,
  },
  setup() {
    const assignmentStore = useAssignmentStore()
    const route = useRoute()

    const items: ComputedRef<Assignment[]> = computed(() => assignmentStore.assignments)
    const loading: ComputedRef<boolean> = computed(() => assignmentStore.assignmentsLoading)
    const serverItemsLength: ComputedRef<number> = computed(() => assignmentStore.assignmentsCount)

    const dataTable = ref()
    const checkedAssignments: Ref<any[]> = ref([])
    const serverOptions: Ref<ServerOptions> = ref({
      page: 1,
      rowsPerPage: 50,
    })

    const headers: Header[] = [
      { text: 'Date', value: 'createdAt' },
      { text: 'Ref', value: 'reference' },
      { text: 'Customer', value: 'customer' },
      { text: 'Details', value: 'details' },
      { text: 'Features', value: 'features' },
      { text: 'Carrier', value: 'carrier' },
      { text: 'Cost', value: 'cost' },
      { text: 'Status', value: 'status' },
      { text: 'Actions', value: 'actions' },
    ]

    const setFilter = () => {
      const companyId = route.query.companyId ? String(route.query.companyId) : null
      assignmentStore.setAssignmentsFilter({
        page: String(serverOptions.value.page),
        limit: String(serverOptions.value.rowsPerPage),
        orderBy: 'createdAt',
        archiveMode: 'active',
        ...(typeof companyId === 'string' ? { companyId } : {}),
      })
    }

    const updateServerOptions = (newOptions: ServerOptions) => {
      serverOptions.value = newOptions
      assignmentStore.updateAssignmentsFilter({
        page: String(newOptions.page),
        limit: String(newOptions.rowsPerPage),
      })
    }

    const updateAssignmentStatus = (assignment: Assignment) => {
      if (!assignment?.id) {
        return
      }
      const index = items.value.findIndex((a) => a.id === assignment.id)
      if (index !== -1 && items.value[index]) {
        items.value[index].status = assignment.status
        items.value[index].itemsCount = assignment.itemsCount
      }
    }
    const hasErrorIcon = (assignment: Assignment) =>
      assignment.labelsErrorsCount > 0 ||
      (assignment.totalErrorsCount > 0 &&
        (assignment.type !== 'single' || (assignment.type === 'single' && !assignment.item?.shipmentNumber)))

    onBeforeUnmount(() => {
      window.Echo.leave('App.Assignments')
      assignmentStore.unmount()
    })

    onBeforeMount(() => {
      setFilter()
      window.Echo.channel('App.Assignments').listen('\\App\\Events\\Assignment\\AssignmentStatusChanged', (data: any) =>
        updateAssignmentStatus(data.assignment),
      )
    })

    return {
      items,
      loading,
      headers,
      dataTable,
      serverOptions,
      serverItemsLength,
      checkedAssignments,
      updateServerOptions,
      hasErrorIcon,
    }
  },
})
</script>
